import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import axios from "axios";
import { toast } from "react-toastify";

import Header from "../../components/Header";
import config from "../../config";
import { tokens } from "../../theme";
import {
  FAMILY_PAYMENT_KIND_LABELS,
  fetchFamilyDetails,
  formatLkr,
} from "./hpcMembershipPricing";
import { isFamilyPackage } from "./hpcFormConfig";
import { FamilyBreakdownTable } from "./FamilyPriceTable";
import HpuPaymentPanel from "./HpuPaymentPanel";
import HpcDeleteMemberDialog from "./HpcDeleteMemberDialog";
import HpcMemberHolds from "./HpcMemberHolds";
import HpcMemberDoorAccess from "./HpcMemberDoorAccess";
import { getAuthToken } from "../../utils/authStorage";
import {
  activateButtonSx,
  backButtonSx,
  cancelButtonSx,
  formatExpiryCountdown,
  formatStatusLabel,
  getDisplayStatus,
  getStatusChipLabel,
  membershipStatusLabel,
  getEffectiveExpiryDate,
  getStatusChipSx,
  hasEstimatedMembershipDates,
  hasMembershipState,
  isStatusDerived,
} from "./hpcAdminStatusStyles";

const formatLabel = (value) =>
  (value || "")
    .toString()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatList = (value) => {
  if (!value) return "—";
  if (Array.isArray(value)) {
    return value.map((item) => formatLabel(item)).join(", ");
  }
  if (typeof value === "object") {
    return Object.entries(value)
      .filter(([, v]) => v === true || v === "yes")
      .map(([key]) => formatLabel(key))
      .join(", ") || "None reported";
  }
  return String(value);
};

const ProfileSection = ({ title, children }) => (
  <Card sx={{ mb: 2 }}>
    <CardContent>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        {title}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {children}
    </CardContent>
  </Card>
);

const Field = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1">{value || "—"}</Typography>
  </Grid>
);

// Family groups only: saved per-member prices and the expected next payment.
const loadFamilyDetails = async (current) => {
  if (!current?.group_id || !isFamilyPackage(current.membership_type)) return null;
  try {
    return await fetchFamilyDetails(current.membership_id);
  } catch {
    return null;
  }
};

const HpcMemberProfile = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { membershipId } = useParams();
  const navigate = useNavigate();
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [groupInfo, setGroupInfo] = useState(null);
  const [familyDetails, setFamilyDetails] = useState(null);
  const [activateOpen, setActivateOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [documentReceived, setDocumentReceived] = useState(true);
  const [referenceNumber, setReferenceNumber] = useState("");
  const [issuedBy, setIssuedBy] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [reviewComments, setReviewComments] = useState("");
  const [activating, setActivating] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const fetchRegistration = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${config.apiBaseUrl}/hpc/admin/registrations/${membershipId}`,
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      setRegistration(response.data.registration);

      const groupId = response.data.registration?.group_id;
      if (groupId) {
        const groupResponse = await axios.get(
          `${config.apiBaseUrl}/hpc/gym-registration/group/${groupId}`
        );
        setGroupInfo(groupResponse.data);
      } else {
        setGroupInfo(null);
      }
      setFamilyDetails(await loadFamilyDetails(response.data.registration));
    } catch (error) {
      console.error("Failed to fetch HPC registration:", error);
      toast.error(error.response?.data?.error || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [membershipId]);

  useEffect(() => {
    fetchRegistration();
  }, [fetchRegistration]);

  const refreshRegistration = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${config.apiBaseUrl}/hpc/admin/registrations/${membershipId}`,
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      setRegistration(response.data.registration);
      setFamilyDetails(await loadFamilyDetails(response.data.registration));
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to refresh profile");
    }
  };

  const handleActivate = async () => {
    setActivating(true);
    try {
      const token = getAuthToken();
      await axios.patch(
        `${config.apiBaseUrl}/hpc/admin/registrations/${membershipId}/activate`,
        { remarks: remarks.trim() || null },
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      // The activation response omits holds/admin status; reload the full profile.
      await refreshRegistration();
      setActivateOpen(false);
      setRemarks("");
      toast.success("Member activated successfully");
    } catch (error) {
      console.error("Failed to activate member:", error);
      toast.error(error.response?.data?.error || "Failed to activate member");
    } finally {
      setActivating(false);
    }
  };

  const handleReviewClearance = async () => {
    if (!documentReceived) {
      toast.error("Medical clearance document must be received before approval.");
      return;
    }
    setReviewing(true);
    try {
      const token = getAuthToken();
      const response = await axios.patch(
        `${config.apiBaseUrl}/hpc/admin/registrations/${membershipId}/review-clearance`,
        {
          document_received: documentReceived,
          reference_number: referenceNumber.trim() || null,
          issued_by: issuedBy.trim() || null,
          issue_date: issueDate || null,
          review_comments: reviewComments.trim() || null,
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      // The clearance response omits holds/admin status; reload the full profile.
      await refreshRegistration();
      setReviewOpen(false);
      setDocumentReceived(true);
      setReferenceNumber("");
      setIssuedBy("");
      setIssueDate("");
      setReviewComments("");
      toast.success(
        response.data.registration?.status === "active"
          ? "Medical clearance approved and member activated"
          : "Medical clearance review recorded"
      );
    } catch (error) {
      console.error("Failed to review medical clearance:", error);
      toast.error(error.response?.data?.error || "Failed to review medical clearance");
    } finally {
      setReviewing(false);
    }
  };

  if (loading) {
    return (
      <Box m="20px">
        <Header title="HPC Member Profile" subtitle="Loading..." />
      </Box>
    );
  }

  if (!registration) {
    return (
      <Box m="20px">
        <Header title="HPC Member Profile" subtitle="Registration not found" />
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/hpc/members")}
          sx={backButtonSx}
        >
          Back to list
        </Button>
      </Box>
    );
  }

  // Workflow actions stay keyed on the stored status; effective status is display-only.
  const canActivate = registration.status === "pending";
  const canReviewClearance = registration.status === "under_review";
  const displayStatus = getDisplayStatus(registration);
  const statusDerived = isStatusDerived(registration);
  const estimatedDates = hasEstimatedMembershipDates(registration);
  const expiryCountdown = formatExpiryCountdown(registration);
  const notRecorded = "Not recorded";
  const expiryUnknown = hasMembershipState(registration) && registration.expiry_known === false
    && (estimatedDates || displayStatus === "active");
  const expiryDateText = expiryUnknown ? notRecorded : registration.expiry_date;
  // Hold-aware responses add the effective expiry (stored expiry + credited frozen days).
  const holdAware = registration.effective_expiry_date !== undefined;
  const effectiveExpiry = getEffectiveExpiryDate(registration);
  const frozenDays = registration.frozen_days_credited || 0;

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Header
          title={registration.full_name}
          subtitle={`Membership ID: ${registration.membership_id}`}
        />
        <Box display="flex" gap={1} alignItems="center">
          <Chip
            label={getStatusChipLabel(registration)}
            sx={getStatusChipSx(displayStatus)}
          />
          {statusDerived && (
            <Typography variant="caption" color="text.secondary">
              Registration: {formatStatusLabel(registration.status)}
            </Typography>
          )}
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/hpc/members")}
            sx={backButtonSx}
          >
            Back to List
          </Button>
          {canReviewClearance && (
            <Button
              variant="contained"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={() => setReviewOpen(true)}
              sx={activateButtonSx}
            >
              Review Clearance
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() => navigate(`/hpc/members/${registration.membership_id}/edit`)}
            sx={backButtonSx}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeleteOpen(true)}
          >
            Delete
          </Button>
          {canActivate && (
            <Button
              variant="outlined"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={() => setActivateOpen(true)}
              sx={activateButtonSx}
            >
              Activate Member
            </Button>
          )}
        </Box>
      </Box>

      {registration.status === "under_review" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Medical clearance is required for this registration. Use the review action to approve it and activate the member.
        </Alert>
      )}

      {statusDerived && registration.status === "active" && displayStatus === "expired" && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {`This membership expired on ${effectiveExpiry || registration.expiry_date}${expiryCountdown ? ` (${expiryCountdown.toLowerCase()})` : ""}. The recorded status is still Active.`}
        </Alert>
      )}

      {["suspended", "cancelled", "inactive", "blacklisted", "grace_period"].includes(displayStatus) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {`Membership status: ${membershipStatusLabel(displayStatus)} (set by an HPU admin). `
            + "The member is not eligible for training until the status is changed"
            + `${displayStatus === "grace_period" ? " and the membership is renewed" : ""}.`}
        </Alert>
      )}

      {displayStatus === "frozen" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {`This membership is frozen${registration.hold_type ? ` (${formatLabel(registration.hold_type)})` : ""}`
            + `${registration.hold_effective_end ? ` until ${registration.hold_effective_end}` : ""}.`
            + `${effectiveExpiry ? ` Credited frozen days extend the effective expiry to ${effectiveExpiry}.` : ""}`}
        </Alert>
      )}

      {registration.status === "active" && registration.registered_by && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {registration.medical_clearance_approved
            ? `Medical clearance approved and member activated by ${registration.registered_by}.`
            : `Activated by ${registration.registered_by}${registration.remarks ? ` — ${registration.remarks}` : ""}`}
        </Alert>
      )}

      <ProfileSection title="Personal Details">
        <Grid container spacing={2}>
          <Field label="Full Name" value={registration.full_name} />
          <Field label="Date of Birth" value={registration.date_of_birth} />
          <Field label="Age" value={registration.age} />
          <Field label="Gender" value={formatLabel(registration.gender)} />
          <Field label="NIC / Passport" value={registration.nic_passport} />
          <Field label="Nationality" value={registration.nationality} />
          <Field label="Occupation" value={registration.occupation} />
          <Field label="Address" value={registration.address} />
        </Grid>
      </ProfileSection>

      <ProfileSection title="Contact">
        <Grid container spacing={2}>
          <Field label="Mobile" value={registration.mobile_number} />
          <Field label="WhatsApp" value={registration.whatsapp_number} />
          <Field label="Email" value={registration.email} />
          <Field label="Emergency Contact" value={registration.emergency_contact_person} />
          <Field label="Relationship" value={registration.emergency_contact_relationship} />
          <Field label="Emergency Number" value={registration.emergency_contact_number} />
        </Grid>
      </ProfileSection>

      <ProfileSection title="Membership">
        <Grid container spacing={2}>
          <Field label="Package" value={formatLabel(registration.membership_type)} />
          <Field label="Start Date" value={registration.membership_start_date} />
          {/* Activation and expiry dates are shown once the member is activated. */}
          {/* Dates filled in by the backend's legacy fallback are not shown as confirmed values. */}
          <Field label="Activation Date" value={estimatedDates ? notRecorded : registration.activation_date} />
          <Field label={holdAware ? "Stored Expiry Date" : "Expiry Date"} value={expiryDateText} />
          {holdAware && (
            <Field label="Effective Expiry Date" value={effectiveExpiry || notRecorded} />
          )}
          {frozenDays > 0 && <Field label="Frozen Days Credited" value={frozenDays} />}
          {expiryCountdown && <Field label="Membership Validity" value={expiryCountdown} />}
          <Field label="Trainer Preference" value={registration.trainer_preference} />
          <Field label="Training Goals" value={formatList(registration.training_goals)} />
          <Field label="Preferred Times" value={formatList(registration.preferred_training_times)} />
        </Grid>
      </ProfileSection>

      {groupInfo && isFamilyPackage(groupInfo.membership_type) && (
        <ProfileSection title="Family Membership">
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Field label="Family Group ID" value={groupInfo.group_id} />
            <Field label="Members" value={String(groupInfo.current_members)} />
            {familyDetails?.group_total != null && (
              <Field label="Total at registration" value={formatLkr(familyDetails.group_total)} />
            )}
            {familyDetails?.expected_payment?.amount != null && (
              <Field
                label={`Expected next payment (${FAMILY_PAYMENT_KIND_LABELS[familyDetails.expected_payment.kind]})`}
                value={formatLkr(familyDetails.expected_payment.amount)}
              />
            )}
          </Grid>
          {familyDetails && (
            <FamilyBreakdownTable
              rows={familyDetails.members}
              totals={[
                [
                  "Saved family total",
                  familyDetails.members.reduce(
                    (sum, m) => sum + (m.net_fee || 0) + (m.admission_fee || 0),
                    0,
                  ),
                ],
              ]}
            />
          )}
          {groupInfo.active && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/hpc/register?groupId=${groupInfo.group_id}`)}
                sx={activateButtonSx}
              >
                Add Family Member
              </Button>
            </Box>
          )}
        </ProfileSection>
      )}

      {groupInfo && !isFamilyPackage(groupInfo.membership_type) && (
        <ProfileSection title="Membership Group">
          <Grid container spacing={2}>
            <Field label="Group ID" value={groupInfo.group_id} />
            <Field
              label="Members"
              value={`${groupInfo.current_members} of ${groupInfo.max_members}`}
            />
          </Grid>
          {groupInfo.active && groupInfo.current_members < groupInfo.max_members && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/hpc/register?groupId=${groupInfo.group_id}`)}
                sx={activateButtonSx}
              >
                Add Member
              </Button>
            </Box>
          )}
        </ProfileSection>
      )}

      <ProfileSection title="Health & PAR-Q">
        <Grid container spacing={2}>
          <Field label="Medical Conditions" value={formatList(registration.medical_conditions)} />
          <Field label="Taking Medication" value={formatLabel(registration.taking_medication)} />
          <Field label="Medication Details" value={registration.medication_details} />
          <Field label="Has Injuries" value={formatLabel(registration.has_injuries)} />
          <Field label="Injury Details" value={registration.injury_details} />
          <Field label="Doctor Recommendations" value={registration.doctor_recommendations} />
          <Field label="PAR-Q Responses" value={formatList(registration.parq_answers)} />
        </Grid>
      </ProfileSection>

      <Card sx={{ mb: 2, border: `2px solid ${colors.greenAccent[500]}` }}>
        <CardContent>
          <Typography variant="h5" fontWeight="bold" gutterBottom color={colors.greenAccent[400]}>
            Payment Details
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Field label="Payment Method" value={formatLabel(registration.payment_method)} />
            <Field label="Membership Fee" value={formatLkr(registration.membership_fee)} />
            <Field label="Registration Fee" value={formatLkr(registration.registration_fee)} />
            <Field label="Total Paid" value={formatLkr(registration.total_amount_paid)} />
            <Field label="Payment Date" value={registration.payment_date} />
            <Field label="Receipt Number" value={registration.receipt_number} />
            <Field label="Documents Acknowledged" value={formatList(registration.documents_ack)} />
          </Grid>
        </CardContent>
      </Card>

      <HpcMemberHolds registration={registration} onChanged={refreshRegistration} />

      <HpcMemberDoorAccess registration={registration} />

      <HpuPaymentPanel
        registration={registration}
        showMemberSummary={false}
        onPaymentCompleted={refreshRegistration}
      />

      <ProfileSection title="Declaration">
        <Grid container spacing={2}>
          <Field label="Declaration Name" value={registration.declaration_name} />
          <Field label="Signature" value={registration.declaration_signature} />
          <Field label="Declaration Date" value={registration.declaration_date} />
          <Field label="Liability Agreed" value={registration.liability_agreed ? "Yes" : "No"} />
          <Field label="Media Consent" value={formatLabel(registration.media_consent)} />
          <Field label="Registered" value={registration.created_at} />
        </Grid>
      </ProfileSection>

      <Dialog open={reviewOpen} onClose={() => !reviewing && setReviewOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Review Medical Clearance</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Record the medical clearance details for {registration.full_name} ({registration.membership_id}) before activation.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={documentReceived}
                onChange={(event) => setDocumentReceived(event.target.checked)}
              />
            }
            label="Medical clearance document received"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Reference number"
            value={referenceNumber}
            onChange={(event) => setReferenceNumber(event.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Issued by (doctor / clinic)"
            value={issuedBy}
            onChange={(event) => setIssuedBy(event.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="date"
            label="Issue date"
            InputLabelProps={{ shrink: true }}
            value={issueDate}
            onChange={(event) => setIssueDate(event.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Review comments"
            value={reviewComments}
            onChange={(event) => setReviewComments(event.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setReviewOpen(false)}
            disabled={reviewing}
            sx={cancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleReviewClearance}
            disabled={reviewing || !documentReceived}
            sx={activateButtonSx}
          >
            {reviewing ? "Saving..." : "Approve & Activate"}
          </Button>
        </DialogActions>
      </Dialog>

      <HpcDeleteMemberDialog
        member={deleteOpen ? registration : null}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => navigate("/hpc/members")}
      />

      <Dialog open={activateOpen} onClose={() => !activating && setActivateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Activate Member</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Confirm that you have verified the payment details for {registration.full_name} (
            {registration.membership_id}). This will set the member status to active.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Remarks (optional)"
            value={remarks}
            onChange={(event) => setRemarks(event.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setActivateOpen(false)}
            disabled={activating}
            sx={cancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleActivate}
            disabled={activating}
            sx={activateButtonSx}
          >
            {activating ? "Activating..." : "Confirm Activation"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HpcMemberProfile;
